import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RunReturn, ScheduleReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Badge } from '../common/Catalyst/badge';
import { Button } from '../common/Catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { Subheading } from '../common/Catalyst/heading';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  formatPercent,
} from '../utils/billingHelpers';
import { runStatus } from './enumLabels';
import GenerateRunsDialog from './GenerateRunsDialog';

interface Props {
  hoaId: string;
  canGenerate: boolean;
}

const PER_PAGE = 25;

/**
 * Every billing run, and the four things you can do with one.
 *
 * A run is a **reviewable object**, not a side effect: generate it,
 * read the lines, post it, and — if it was wrong — reverse the whole
 * set. Collapsing that into one "bill everyone" button throws away the
 * only chance anybody gets to look before homeowners are charged.
 */
const RunList: React.FC<Props> = ({ hoaId, canGenerate }) => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<RunReturn[]>([]);
  const [schedules, setSchedules] = useState<ScheduleReturn[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, s] = await Promise.all([
        billingService.listRuns({ hoaId, page, perPage: PER_PAGE }),
        billingService.listSchedules({ hoaId }),
      ]);
      setRuns(r.data.runs);
      setTotal(r.data.total);
      setSchedules(s.data.schedules);
    } catch (err) {
      console.error('Error loading runs:', err);
      setError(billingErrorMessage(err, "Couldn't load the billing runs."));
    } finally {
      setLoading(false);
    }
  }, [hoaId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const held = runs.filter((r) => r.held_reason);
  const pending = runs.filter((r) => r.status === 'pending');
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  if (loading && runs.length === 0) return <LoadingSpinner />;

  return (
    <>
      {error && (
        <div className="mb-4">
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load the billing runs"
            alertMessage={error}
            isVisible={!!error}
            onClose={() => setError(null)}
            timeout={10000}
            isInline
          />
        </div>
      )}

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Subheading>Billing runs</Subheading>
          <Text className="mt-1">
            Each period&rsquo;s charges, as something you can read before it
            reaches anybody. Generating writes nothing to a ledger; posting
            does.
          </Text>
        </div>
        {canGenerate && (
          <Button color="sky" onClick={() => setGenerating(true)}>
            Generate runs
          </Button>
        )}
      </div>

      {held.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
          <Subheading>
            {held.length} run{held.length === 1 ? '' : 's'} held for review
          </Subheading>
          <Text className="mt-1">
            A scheduled run whose total moved more than this association&rsquo;s
            threshold stops rather than posting.{' '}
            <strong>That is the guard working, not a failure</strong> — late
            beats wrong, because a held run is one click from correct while a
            wrongly posted one means reversing every entry after the coupons
            have gone out.
          </Text>
          <ul className="mt-2 list-disc pl-5">
            {held.map((r) => (
              <li key={r.id} className="text-sm">
                {formatBillingDate(r.period_start)}: {r.held_reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending.length > 0 && held.length === 0 && (
        <Text className="mb-4">
          {pending.length} run{pending.length === 1 ? '' : 's'} generated and
          not yet posted. Nothing has been charged until you post them.
        </Text>
      )}

      {runs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-zinc-300 dark:border-zinc-700 p-6">
          <Text>
            No billing run has been generated for this association yet.
            {schedules.some((s) => s.status === 'active')
              ? ' There is an active schedule, so generating will produce every period since its anchor.'
              : ' There is no active dues schedule, so there is nothing to generate from yet.'}
          </Text>
        </div>
      ) : (
        <>
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Period</TableHeader>
                <TableHeader>Due</TableHeader>
                <TableHeader className="text-right">Homes</TableHeader>
                <TableHeader className="text-right">Total</TableHeader>
                <TableHeader className="text-right">Change</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader> </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    {formatBillingDate(r.period_start)} –{' '}
                    {formatBillingDate(r.period_end)}
                  </TableCell>
                  <TableCell>{formatBillingDate(r.due_date)}</TableCell>
                  <TableCell className="text-right">{r.charge_count}</TableCell>
                  <TableCell className="text-right">
                    {formatMoney(r.total_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.variance_percent
                      ? formatPercent(r.variance_percent)
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge color={runStatus.color(r.status)}>
                        {runStatus.label(r.status)}
                      </Badge>
                      {r.unattended && <Badge color="zinc">Scheduled</Badge>}
                      {r.held_reason && <Badge color="amber">Held</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      plain
                      onClick={() =>
                        navigate(`/hoa/${hoaId}/billing/runs/${r.id}`)
                      }
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between">
            <Text>
              {total} run{total === 1 ? '' : 's'} · page {page} of {lastPage}
            </Text>
            <div className="flex gap-2">
              <Button
                plain
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                plain
                disabled={page >= lastPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <GenerateRunsDialog
        isOpen={generating}
        onClose={() => setGenerating(false)}
        onGenerated={load}
        schedules={schedules}
      />
    </>
  );
};

export default RunList;
